import React from 'react';
import { CropInnerCardAttributes } from './types/CropInnerCardAttributes';
import { SILAGE_CORN_CROP_CODE, COTTON_CROP_CODE, SUGARCANE_CROP_CODE } from '../../../constants/cropCodes'
import {
  LocalizedStat,
  getLocalizedArea,
  getLocalizedAverageLint,
  getLocalizedAverageMoisture,
  getLocalizedAverageYield,
  getLocalizedDryMassForSilage,
  getLocalizedDryYieldForSilage,
  getLocalizedStdYieldForSilage,
  getLocalizedTotalLint,
  getLocalizedTotalWeight,
  getLocalizedWetMassForSilage,
  getLocalizedWetYieldForSilage
} from './statsHelper';
import { CardLabelValue } from '../CardLabelValue/CardLabelValue';

type CardStructureMap = Record<string, (params: CropInnerCardAttributes) => LocalizedStat[]>;
const cardsStructure = (): CardStructureMap => ({
  [SILAGE_CORN_CROP_CODE]: ({isFieldYieldAnalysisAvailable, harvestData, cropCode, displayAdditionalSilageData: displayAdditionalSilageDate}: CropInnerCardAttributes) => [
    ...(displayAdditionalSilageDate ? [
        getLocalizedStdYieldForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.stdYield',
          cropCode,
        }),
        getLocalizedAverageMoisture({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.cropMoisture',
          cropCode,
        }),
         getLocalizedAverageMoisture({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.dryMatter',
          cropCode,
          dryMatter: true
        }),
        getLocalizedDryYieldForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.dryYield',
          cropCode,
        }),
        getLocalizedDryMassForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'regions.report.modal.dryWeight',
          cropCode,
        }),
        getLocalizedWetYieldForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.wetYield',
          cropCode,
        }),
        getLocalizedWetMassForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'card.harvest.wetWeight',
          cropCode,
        })
      ] : [
        getLocalizedWetYieldForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'regions.report.modal.avgWetYield',
          cropCode,
        }),
        getLocalizedDryYieldForSilage({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'regions.report.modal.avgDryYield',
          cropCode,
        }),
        getLocalizedAverageMoisture({
          harvestData,
          isFieldYieldAnalysisAvailable,
          translationKey: 'layer.moisture',
          cropCode,
        }),
      ]
    ),
  ],

  [COTTON_CROP_CODE]: ({isFieldYieldAnalysisAvailable, harvestData, cropCode, cropNominalWeight}: CropInnerCardAttributes) => [
    getLocalizedAverageLint({
      harvestData,
      isFieldYieldAnalysisAvailable,
      translationKey: 'regions.report.modal.cottonAverageYield',
      cropCode,
      cropNominalWeight,
    }),

    ...[
      getLocalizedTotalLint({
        harvestData,
        isFieldYieldAnalysisAvailable,
        translationKey: 'card.harvest.avgYield.cottonTotalLint',
        cropCode,
        cropNominalWeight,
      })
    ],
  ],

  [SUGARCANE_CROP_CODE]: ({isFieldYieldAnalysisAvailable, harvestData, cropCode}: CropInnerCardAttributes) => [
    getLocalizedAverageYield({
      harvestData,
      isFieldYieldAnalysisAvailable,
      translationKey: 'card.harvest.avgYield',
      cropCode,
    }),
    
    ...[
      getLocalizedTotalWeight({
        harvestData,
        isFieldYieldAnalysisAvailable,
        translationKey: 'card.harvest.totalWeight',
        cropCode,
      })
    ]
  ],

  'default': ({isFieldYieldAnalysisAvailable, harvestData, cropCode, cropNominalWeight}: CropInnerCardAttributes) => [
    getLocalizedAverageYield({
      harvestData,
      isFieldYieldAnalysisAvailable,
      translationKey: 'card.harvest.avgYield',
      cropCode,
      cropNominalWeight,
    }),
    getLocalizedAverageMoisture({
      harvestData,
      isFieldYieldAnalysisAvailable,
      translationKey: 'layer.moisture',
      cropCode,
      cropNominalWeight,
    })
  ]
})

export default function useHarvestCardElements(params: CropInnerCardAttributes) {
  const { cropCode } = params;

  const cardStructureInstance = cardsStructure()

  let cardElements: LocalizedStat[];
  if (cropCode && typeof cardStructureInstance[cropCode] !== 'undefined') {
    cardElements = cardStructureInstance[cropCode](params);
  } else {
    cardElements = cardStructureInstance.default(params);
  }

  const cardElementsWithCommons = cardElements.concat([
    getLocalizedArea({
      harvestData: params.harvestData,
      isFieldYieldAnalysisAvailable: params.isFieldYieldAnalysisAvailable,
      translationKey: 'card.harvest.harvested',
      cropCode,
    }),
  ]);

  return cardElementsWithCommons.map(({testId, label, value}) => (
    <CardLabelValue testId={testId} label={label} key={`card-element-${testId}`}>
      {value}
    </CardLabelValue>
  ));
}